// Meetings (U22, U23, U24, T59, T63). The calls this person may list, and what Cobrain made of each.
//
// Two surfaces under /meetings, routed by App.tsx's surfaceOf(): the list, and one meeting at
// /meetings/:id. The meeting page is Fathom's shape (U23, FathomUi1.png is the specification):
// the recording top left with Summary and Transcript tabs under it; title, date, attendees with
// their job titles, the related meeting and the action items down the right. The summary a
// person reads first is the brief -- purpose, key takeaways, topics (U24) -- with the long form
// kept underneath, so nothing already paid for is lost.
//
// The look is U28, "Match centre" (module 10 VISUAL-SYSTEM.md), rebuilt 2026-09-20 in the grammar
// the upgraded pages already speak rather than in new tokens on the old layout: the page head is
// Tasks', the list is bands of rows on one surface with the older bands folded, a fixed time is a
// clock plate, and Play is the page's one Score-yellow thing. What is ON each page is unchanged.
//
// Where this page departs from the screenshot, and why, so nobody "fixes" it back:
//   * No checkboxes on the action items. Nothing is ever marked complete on inference (D60), and
//     a box that ticks nothing is a lie. A task is what the extraction lane made, with evidence.
//   * No "Ask" tab yet. Cobrain's answer surface exists elsewhere and is not wired to one meeting;
//     a dead tab would be worse than none.
//   * A timestamp on an action item is where Cobrain FOUND the words in the transcript, or absent;
//     it is never guessed (brief.ts). It opens the transcript at that turn, since the Microsoft
//     embed cannot be seeked from outside.
//   * A key takeaway's bold claim goes to where it was discussed (U196, B1720): it plays the call
//     from that minute when the recording is a file (Cobrain's own copy, or the Notetaker's), opens
//     Fathom there, and otherwise opens the transcript at that turn. A claim with no minute stays
//     plain: a link to the wrong moment is worse than none.
//
// Three honesty rules this component holds:
//   * A meeting with no summary says why in words; a summary with no brief shows the long form
//     and says the brief is not written, never a blank.
//   * The transcript shown is corpus.signal.body, so a passage the rails hid is a marker here
//     because it is a marker in the column. This file does no hiding of its own (R1, module 05).
//   * The recording link is minted when Play is pressed and is good for five minutes; the page
//     asks again rather than keeping it, and says when it has run out.

import { ActionButton, ActionInput, ActionSelect } from './parts/ActionButton.js';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useLiveRefresh } from './live.js';
import { ApiError, Unauthenticated } from './api.js';
import { dayMonth } from './day.js';
import {
  addTask, askHowItWentAgain, captureWords, clockWords, companyMeetings as fetchCompanyMeetings, fathomAt, hasTranscript, lengthWords, mateWords, matesByCalls, meeting as fetchMeeting,
  meetings as fetchMeetings, recordingLink, setVisibleTo, visibleTo, firstNameOf, type VisibleSwitch, keepState, setKeep, type KeepState,
  stampWords, teamMeetings as fetchTeamMeetings, thumbnailUrl, viaWords, whenWords,
  type MeetingActionItem, type MeetingDetail, type MeetingHowItWent, type MeetingItemTask, type MeetingRow, type MeetingsView, type RecordingView, type TeamMeetingsView,
} from './meetings/api.js';
import { isDay, todayInParis } from './engineering/period.js';
import { FilterBar, matchesWords, tally } from './parts/FilterBar.js';
import { useFilterParams, withParams, type Params } from './find/params.js';
import { fetchRundown, type RundownView } from './company/api.js';
import { Rundown } from './company/Rundown.js';
import './meetings.css';
import { LineOrName } from './parts/CompanyLine.js';
import { useCompanyLines } from './company/lines.js';

type ListLoad = { kind: 'loading' } | { kind: 'ready'; view: MeetingsView } | { kind: 'error'; message: string };
type TeamLoad = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; view: TeamMeetingsView } | { kind: 'error'; message: string };
type ListTab = 'mine' | 'team' | 'company';
/** The two tabs drawn by one list (B1649): the calls of the reader's team, and every call in the company. */
type PeopleTab = Exclude<ListTab, 'mine'>;
type OneLoad = { kind: 'loading' } | { kind: 'ready'; view: MeetingDetail } | { kind: 'error'; message: string };

/**
 * One loader read two ways. A first read (and Try again) may empty the page to a skeleton; a live
 * re-read when he comes back to the window must not, or the list he was looking at flashes away and
 * returns for nothing. The quiet one hands back its promise, so the live watcher can tell when it has
 * finished and never has two in the air at once.
 */
function useReadTwoWays<V>(
  fetcher: () => Promise<V>,
  setLoad: (l: { kind: 'loading' } | { kind: 'ready'; view: V } | { kind: 'error'; message: string }) => void,
): { read: () => void; quiet: () => Promise<void> } {
  const quiet = useCallback(
    () => fetcher().then(
      (view) => setLoad({ kind: 'ready', view }),
      (e: unknown) => setLoad({ kind: 'error', message: messageOf(e) }),
    ),
    [fetcher, setLoad],
  );
  const read = useCallback(() => { setLoad({ kind: 'loading' }); void quiet(); }, [quiet, setLoad]);
  return { read, quiet };
}


function messageOf(e: unknown): string {
  if (e instanceof Unauthenticated) throw e;
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : 'something went wrong';
}

function Failed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="mtg-failed" role="alert">
      <p>{message}</p>
      <ActionButton reading type="button" className="mtg-retry" onClick={onRetry}>Try again</ActionButton>
    </div>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: 'ext' | 'quiet' }) {
  return <span className={tone ? `tag ${tone}` : 'tag'}>{children}</span>;
}

/** This page's own address at a second of the call (U196: ?t=SECONDS, as the call page takes it), keeping what else it carries. */
function hereAt(ms: number): string {
  const q = new URLSearchParams(window.location.search);
  q.set('t', String(Math.floor(ms / 1000)));
  return `${window.location.pathname}?${q.toString()}`;
}

const PlayMark = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>;

// -------------------------------------------------------------------------------------------
// The list: bands, and finding one in a long list
// -------------------------------------------------------------------------------------------

/**
 * The band a meeting falls in. The live list is nearly two hundred calls over four months, so it
 * is read the way a diary is: today and this week in full, and the months behind them folded with
 * their count until they are asked for.
 */
export function bandOf(startedAt: string, now: Date): { key: string; label: string; open: boolean } {
  const d = new Date(startedAt);
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  // Days counted midnight to midnight and rounded, never hours (a day against a day, B1300): a
  // clock change makes one day 23 or 25 hours long and the rounding absorbs it.
  const days = Math.round((midnight(now) - midnight(d)) / 86_400_000);
  // "This week" and "Last week" are calendar weeks starting on Monday, as their labels say (BUGS.md
  // M8, M36): on a Monday last Thursday is last week, not earlier this week, and the Thursday before
  // it is in its month's band. `sinceMonday` is how many days today is past this week's Monday.
  const sinceMonday = (now.getDay() + 6) % 7;
  if (days <= 0) return { key: 'today', label: 'Today', open: true };
  if (days === 1) return { key: 'yesterday', label: 'Yesterday', open: true };
  if (days <= sinceMonday) return { key: 'week', label: 'Earlier this week', open: true };
  if (days <= sinceMonday + 7) return { key: 'lastweek', label: 'Last week', open: true };
  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return { key, label: d.toLocaleDateString('en-GB', { month: 'long', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' }), open: false };
}

/**
 * Inside a band that names the day, the row shows the time alone; a month band shows the date.
 * The live dot is literal here: it means this call is running now, not that it happened today.
 * A finished call wearing a red dot says the meeting is in progress, which is the one thing red
 * must never say wrongly on this surface.
 */
function rowWhen(startedAt: string, endedAt: string | null, minutes: number | null, bandKey: string, now: Date): { text: string; live: boolean } {
  const d = new Date(startedAt);
  const ends = endedAt ? new Date(endedAt).getTime() : minutes ? d.getTime() + minutes * 60_000 : d.getTime();
  const live = d.getTime() <= now.getTime() && now.getTime() <= ends;
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (bandKey === 'today') return { text: time, live };
  if (bandKey === 'yesterday') return { text: time, live: false };
  if (bandKey === 'week' || bandKey === 'lastweek') {
    return { text: `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`, live: false };
  }
  return { text: whenWords(startedAt, now), live: false };
}

/**
 * The hero (T63, Mike: "a hero image of the video recording"). A Teams recording has a still
 * Graph makes of the file; the Notetaker's has none yet, so its tile is the play mark on a
 * dark field. A meeting with no recording keeps the column's width and shows nothing in it --
 * a tile for a video that does not exist is the kind of lie this page does not tell, and a
 * ragged left edge is what the list had before.
 */
/** Whether a recording can be reached from this row at all (Motion's stayed with Motion). */
const playable = (vendor: string | null): boolean => vendor === 'recall' || vendor === 'teams_graph' || vendor === 'fathom';

/**
 * Whether this row's tile may promise a recording (BUGS.md M66, B2506): the API's answer for this reader when it has
 * one, else the vendor's rule. Mike, by pop-up, 22 Oct 2026, "\"No recording\" look (Recommended)": a call with nothing
 * to play for you (a recording Microsoft no longer has, one never found, a colleague's Notetaker call with no copy)
 * gets the lines tile and 'No recording', and switches to the still or the play mark once a copy arrives.
 */
export const rowPlays = (m: Pick<MeetingRow, 'vendor' | 'playable'>): boolean =>
  typeof m.playable === 'boolean' ? m.playable : playable(m.vendor);

/**
 * Where a meeting's still is, or null for a call that can have none. ONE definition, because the
 * list's tile and the meeting page's plate held two: B1058 gave the route Cobrain's own frame for
 * any vendor and taught the tile to ask for it, and the plate went on asking only for Teams. The
 * route decides what exists; this decides only whether it is worth asking, and a 404 draws exactly
 * what a call with no picture drew before.
 */
export function stillUrl(vendor: string | null, id: string): string | null {
  return playable(vendor) ? thumbnailUrl(id) : null;
}

/**
 * The hero (T63, Mike: "a hero image of the video recording"; kept, 2026-09-20: "You have killed
 * the meeting image heros???"). Graph makes a still of a Teams recording; the Notetaker's and
 * Fathom's have none, so those are the play mark on the graphite tile, which is dark in both
 * palettes -- the clock plate's own colours flip at night and made eight near-white slabs of it.
 * A meeting with nothing to play keeps the slot so the rows line up and says so in words.
 */
function Hero({ m }: { m: MeetingRow }) {
  const [gone, setGone] = useState(false);
  // B1058: every recording Cobrain can reach now has a picture, not only the Teams ones. The
  // Notetaker's comes from Cobrain's own copy (migration 156); the route answers 404 where there
  // is none yet, and `gone` puts the play mark back, which is what the row showed before.
  const plays = rowPlays(m);
  const still = plays ? stillUrl(m.vendor, m.id) : null;
  if (still && !gone) {
    return <img className="mtg-hero" src={still} alt="" onError={() => setGone(true)} />;
  }
  if (plays) return <span className="mtg-hero mtg-hero-blank" aria-hidden="true"><PlayMark /></span>;
  return (
    <span className="mtg-hero mtg-hero-none" aria-hidden="true">
      <svg viewBox="0 0 24 24"><path d="M5 5h14M5 9.5h14M5 14h10M5 18.5h7" /></svg>
    </span>
  );
}

/**
 * One meeting on a list. On the My Team tab (T73, B444) `team` replaces two tags: who from the
 * team was on it stands where "Shared with your group" would, and the company's name where
 * "External" would, since every row there is a customer's call.
 */
export function Row({ m, onOpen, bandKey, now, team }: {
  m: MeetingRow; onOpen: (id: string) => void; bandKey: string; now: Date;
  team?: { mates: string[]; company: string | null };
}) {
  const length = lengthWords(m.minutes);
  const via = team ? mateWords(team.mates) : viaWords(m.via);
  const when = rowWhen(m.startedAt, m.endedAt, m.minutes, bandKey, now);
  return (
    <ActionButton reading type="button" className="mtg-row" onClick={() => onOpen(m.id)}>
      <Hero m={m} />
      <span className="t">
        <span className="tt">{m.title ?? 'Untitled meeting'}</span>
        {m.headline
          ? <span className="hl">{m.headline}</span>
          : <span className="hl quiet">{
            when.live ? 'Being recorded now'
              : hasTranscript(m.transcriptStatus) ? 'Not summarised'
                : 'No transcript arrived, so there is nothing to summarise'
          }</span>}
        <span className="sub">
          <Tag tone="quiet">{captureWords(m.vendor)}</Tag>
          {/* The counterparty is the one outlined chip, so a customer's call is read at a glance. */}
          {team?.company ? <Tag tone="ext">{team.company}</Tag> : m.isExternal ? <Tag tone="ext">External</Tag> : null}
          {!rowPlays(m) ? <Tag tone="quiet">No recording</Tag> : null}
          {(m.others ?? []).length > 0
            ? <Tag tone="quiet">{`+ ${m.others.length} more ${m.others.length === 1 ? 'recording' : 'recordings'}`}</Tag>
            : null}
          {/* Who was on it, and why it is on your list, are facts about the meeting, not categories: plain meta. */}
          {via ? <span className="via">{via}</span> : null}
        </span>
      </span>
      <span className="len">{length ?? ''}</span>
      <span className={when.live ? 'plate' : 'plate quiet'}>{when.live ? <i /> : null}{when.text}</span>
    </ActionButton>
  );
}

/** The bands of a list, with the months behind this fortnight folded until they are asked for. */
/** The company a call with people from outside is with: the first of theirs the list names. */
function companyOf(m: MeetingRow): { id: string; name: string } | null {
  return m.isExternal ? (m.companies ?? [])[0] ?? null : null;
}

function Bands({ rows, onOpen, teamOf, openAll, store }: {
  rows: MeetingRow[]; onOpen: (id: string) => void;
  teamOf?: (m: MeetingRow) => { mates: string[]; company: string | null };
  openAll: boolean;
  /** The store the lines are read in (U353): each call with people from outside carries its company's line. */
  store?: string;
}) {
  const now = useMemo(() => new Date(), []);
  const [shut, setShut] = useState<Set<string>>(new Set());
  const bands: Array<{ key: string; label: string; open: boolean; rows: MeetingRow[] }> = [];
  for (const m of rows) {
    const b = bandOf(m.startedAt, now);
    const last = bands[bands.length - 1];
    if (last && last.key === b.key) last.rows.push(m);
    else bands.push({ ...b, rows: [m] });
  }
  const toggle = (k: string) => setShut((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  // Lines only for the bands that are open: a folded band's rows are not drawn.
  const shownIds = bands.filter((b) => openAll || (b.open ? !shut.has(b.key) : shut.has(b.key))).flatMap((b) => b.rows.map((m) => companyOf(m)?.id));
  const lines = useCompanyLines(store, shownIds);
  return (
    <>
      {bands.map((b) => {
        // A search opens every band that has a hit: a fold would hide the thing being looked for.
        const open = openAll || (b.open ? !shut.has(b.key) : shut.has(b.key));
        const words = `${b.rows.length} ${b.rows.length === 1 ? 'meeting' : 'meetings'}`;
        return (
          <section className="mtg-band" key={b.key}>
            {open
              ? (
                <ActionButton reading type="button" className="mtg-bandh" aria-expanded={true} onClick={() => toggle(b.key)}>
                  <span className="caret open" aria-hidden="true" />
                  <h2>{b.label}</h2>
                  <span className="c">{words}</span>
                </ActionButton>
              )
              : (
                <ActionButton reading type="button" className="mtg-bandh folded" aria-expanded={false} onClick={() => toggle(b.key)}>
                  <span className="caret" aria-hidden="true" />
                  <h2>{b.label}</h2>
                  <span className="c">{words}</span>
                  <span className="sp" />
                </ActionButton>
              )}
            {open
              ? (
                <div className="mtg-tbl">
                  {b.rows.map((m) => {
                    const co = companyOf(m);
                    return (
                      <Fragment key={m.id}>
                        <Row m={m} onOpen={onOpen} bandKey={b.key} now={now} {...(teamOf ? { team: teamOf(m) } : {})} />
                        {/* The company's line under the call (U353; the drawing's Meetings list), its name a link (X30). */}
                        {co ? <div className="mtg-line"><LineOrName id={co.id} name={co.name} line={lines[co.id.toLowerCase()]} /></div> : null}
                      </Fragment>
                    );
                  })}
                </div>
              )
              : null}
          </section>
        );
      })}
    </>
  );
}

/** What the bar narrows by, kept in the address (U228; pipeline F5, B1957): the same on every tab. */
const FIND = ['q', 'company', 'person', 'source', 'ext', 'from', 'to'] as const;
type Find = Params<(typeof FIND)[number]>;
const NOTHING_SET: Find = { q: '', company: '', person: '', source: '', ext: '', from: '', to: '' };
const isSet = (f: Find): boolean => FIND.some((k) => f[k] !== '');

/** A call's day, as the page's dates are read: in Paris, where every time the page shows is. */
const dayOf = (iso: string): string => todayInParis(Date.parse(iso));

/**
 * The rows the bar leaves (SEARCH-AND-FILTER-PLAN-2026-10-04.md section 3, the Meetings row): every word typed, in
 * its title, what it was about, where it came from, who was on it and their companies; the company and the person
 * chosen; the source; a customer's calls; and the calls from the first day to the last, either left open, a pair
 * typed backwards read the right way round, and a date that is not one ignored.
 */
function narrowBy<R extends MeetingRow & { mates?: string[]; company?: string | null }>(rows: readonly R[], f: Find): R[] {
  let lo = isDay(f.from) ? f.from : '';
  let hi = isDay(f.to) ? f.to : '';
  if (lo !== '' && hi !== '' && lo > hi) [lo, hi] = [hi, lo];
  return rows.filter((m) => {
    if (f.company !== '' && !(m.companies ?? []).some((c) => c.id === f.company)) return false;
    if (f.person !== '' && !(m.people ?? []).some((x) => x.id === f.person)) return false;
    if (f.source !== '' && (m.vendor ?? 'other') !== f.source) return false;
    if (f.ext !== '' && !m.isExternal) return false;
    if (lo !== '' || hi !== '') {
      const day = dayOf(m.startedAt);
      if ((lo !== '' && day < lo) || (hi !== '' && day > hi)) return false;
    }
    return matchesWords(f.q, m.title, m.headline, captureWords(m.vendor), m.company, ...(m.mates ?? []),
      ...(m.people ?? []).map((x) => x.name), ...(m.companies ?? []).map((c) => c.name));
  });
}

/**
 * The bar over every tab's list (U228; pipeline F5, B1957): find, By company, By person and Where it came from, each
 * offering what the tab's own list holds with how many calls carry it, then From and To, and a customer's calls.
 * The count shows once anything is set; the tab's own counts above it say the rest.
 */
function MeetingsBar({ f, setF, rows, shown }: {
  f: Find; setF: (changes: Partial<Find>) => void; rows: readonly MeetingRow[]; shown: number;
}) {
  const sources = useMemo(() => tally(rows, (m) => ({ id: m.vendor ?? 'other', name: captureWords(m.vendor) })), [rows]);
  const companies = useMemo(() => tally(rows, (m) => m.companies ?? []), [rows]);
  const people = useMemo(() => tally(rows, (m) => m.people ?? []), [rows]);
  const externals = rows.filter((m) => m.isExternal).length;
  const today = todayInParis(Date.now());
  const date = (key: 'from' | 'to', label: string) => (
    <label className={isDay(f[key]) ? 'tg date set' : 'tg date'}>
      {label}
      <ActionInput reading type="date" value={isDay(f[key]) ? f[key] : ''} max={today} aria-label={`${label}, the day of the call`}
        onChange={(e) => setF({ [key]: e.target.value } as Partial<Find>)} />
    </label>
  );
  return (
    <FilterBar find="Find a meeting" q={f.q} onFind={(q) => setF({ q })}
      shown={shown} total={isSet(f) ? rows.length : null} noun={['meeting', 'meetings']}
      onClearAll={() => setF(NOTHING_SET)}
      extraSet={f.ext !== '' || f.from !== '' || f.to !== ''}
      extra={externals > 0 || f.ext !== ''
        ? <ActionButton reading type="button" className={f.ext !== '' ? 'tg on' : 'tg'} aria-pressed={f.ext !== ''} onClick={() => setF({ ext: f.ext !== '' ? '' : '1' })}>With a customer <em>{externals}</em></ActionButton>
        : null}
      picks={[
        { label: 'By company', find: 'Find a company', any: 'Any company', value: f.company, options: companies, onChange: (company) => setF({ company }) },
        { label: 'By person', find: 'Find a person', any: 'Anyone', value: f.person, options: people, onChange: (person) => setF({ person }) },
        { label: 'Where it came from', find: 'Find a source', any: 'Every source', value: f.source, options: sources, onChange: (source) => setF({ source }) },
      ]}
      after={<>{date('from', 'From')}{date('to', 'To')}</>} />
  );
}

function tabOfLocation(): ListTab {
  const t = new URLSearchParams(window.location.search).get('tab');
  return t === 'team' || t === 'company' ? t : 'mine';
}

/**
 * Where a list tab lives, so Back from a meeting returns to it; `who` is the team-mate it was narrowed to. What the bar
 * holds is kept: a company chosen on Mine is still chosen on Company (B1957).
 */
function tabPath(t: ListTab, who: string | null = null): string {
  return `/meetings${withParams(window.location.search, { tab: t === 'mine' ? '' : t, who: who ?? '' })}`;
}

/**
 * What differs between My Team and Company, and nothing else does (B1649: Mike asked for the
 * Company tab in "all of the exact same formatting and style as team meetings", so both are one
 * list and this is the whole of the difference).
 */
const PEOPLE_TAB: Record<PeopleTab, {
  read: () => Promise<TeamMeetingsView>;
  count: (n: number) => string;
  empty: { title: string; body: string };
}> = {
  team: {
    read: fetchTeamMeetings,
    count: () => 'your team had without you',
    empty: {
      title: 'No calls from your team yet.',
      body: 'A call appears here when someone in your team was on it and you were not, and the rails share it with your group. Calls you were on are under Mine.',
    },
  },
  // Module 03 T99, module 10 U182: every call on Mine and My Team in one list, for the two people
  // the server names. "Who was on it" names everyone from the company, the reader included.
  company: {
    read: fetchCompanyMeetings,
    count: () => 'across the company',
    empty: {
      title: 'No calls recorded yet.',
      body: 'A call appears here once Cobrain has it — the Notetaker, Teams or an imported recording — and it has been classified, whoever in the company was on it.',
    },
  },
};

/**
 * The My Team tab's list (T73; module 05 R38 to R40; B444; B1521): the calls a team-mate was on and
 * this person was not. The exact complement of Mine -- every meeting the rails give them is on one
 * tab or the other, and no meeting is on both.
 * Read when the tab is first chosen, not before, so a person who never opens it costs nothing.
 */
/** Who the My Team list is narrowed to, from the address (?who=), so Back from a meeting keeps it. */
function whoOfLocation(): string | null {
  const w = new URLSearchParams(window.location.search).get('who');
  return w && w.trim() !== '' ? w : null;
}

function TeamList({ onOpen, kind, f, setF }: { onOpen: (id: string) => void; kind: PeopleTab; f: Find; setF: (changes: Partial<Find>) => void }) {
  const words = PEOPLE_TAB[kind];
  const [load, setLoad] = useState<TeamLoad>({ kind: 'idle' });
  const [who, setWho] = useState<string | null>(whoOfLocation);
  const pick = (w: string | null) => {
    setWho(w);
    window.history.replaceState(null, '', tabPath(kind, w));
  };
  const { read, quiet } = useReadTwoWays(words.read, setLoad);
  useEffect(read, [read]);
  useLiveRefresh(quiet);
  if (load.kind === 'idle' || load.kind === 'loading') return <div className="mtg-skeleton" aria-hidden="true"><i /><i /><i /></div>;
  if (load.kind === 'error') return <Failed message={load.message} onRetry={read} />;
  const all = load.view.meetings;
  const mates = matesByCalls(all);
  // A name in the address that is not on the list (a stale link) shows everyone rather than nothing.
  const narrowed = who !== null && mates.some((x) => x.name === who) ? who : null;
  const byMate = narrowed ? all.filter((m) => m.mates.includes(narrowed)) : all;
  const rows = narrowBy(byMate, f);
  const mateOf = new Map(all.map((m) => [m.id, { mates: m.mates, company: m.company }]));
  return (
    <>
      {all.length > 0
        ? (
          <>
            <div className="mtg-counts">
              <span><b className="big">{byMate.length}</b> {byMate.length === 1 ? 'call' : 'calls'} {narrowed ? `${narrowed} was on` : words.count(byMate.length)}</span>
            </div>
            {/* BUGS.md M30: a list cut at its ceiling says so, as Mine's does (M18). */}
            {load.view.more ? <p className="mtg-note">The newest {all.length} are listed; older calls are not, and the counts and filters cover these only.</p> : null}
            {mates.length > 1
              ? (
                <div className="mtg-chips" role="group" aria-label="Show the calls of">
                  <span className="mtg-chips-l">Who was on it</span>
                  <ActionButton reading type="button" className={narrowed === null ? 'chip on' : 'chip'} aria-pressed={narrowed === null} onClick={() => pick(null)}>
                    Everyone <b>{all.length}</b>
                  </ActionButton>
                  {mates.map((x) => (
                    <ActionButton reading key={x.name} type="button" className={narrowed === x.name ? 'chip on' : 'chip'} aria-pressed={narrowed === x.name} onClick={() => pick(x.name)}>
                      {x.name} <b>{x.calls}</b>
                    </ActionButton>
                  ))}
                </div>
              )
              : null}
            <MeetingsBar f={f} setF={setF} rows={byMate} shown={rows.length} />
          </>
        )
        : null}
      {all.length === 0
        ? (
          <div className="mtg-empty">
            <h2>{words.empty.title}</h2>
            <p>{words.empty.body}</p>
          </div>
        )
        : rows.length === 0
          ? (
            <div className="mtg-empty">
              <h2>Nothing here answers that.</h2>
              <p>{byMate.length} {byMate.length === 1 ? 'call was' : 'calls were'} read; none of them matches what you asked for. Clear the search or choose every source.</p>
            </div>
          )
          : <Bands rows={rows} onOpen={onOpen} teamOf={(m) => mateOf.get(m.id) ?? { mates: [], company: null }} openAll={isSet(f)} store={load.view.store.tenantId} />}
    </>
  );
}

export function MeetingsList({ onOpen }: { onOpen: (id: string, from: ListTab) => void }) {
  const [load, setLoad] = useState<ListLoad>({ kind: 'loading' });
  const [tab, setTab] = useState<ListTab>(tabOfLocation);
  const [f, setF] = useFilterParams(FIND);
  const { read, quiet } = useReadTwoWays(fetchMeetings, setLoad);
  useEffect(read, [read]);
  useLiveRefresh(quiet);
  const choose = (t: ListTab) => {
    setTab(t);
    // The tab rides in the address so Back from a meeting returns to it; replaced, not pushed, so
    // switching tabs is not a step Back has to undo.
    window.history.replaceState(null, '', tabPath(t));
  };

  const head = (tabs: React.ReactNode) => (
    <div className="mtg-top"><h1 className="mtg-title">Meetings</h1>{tabs}</div>
  );
  if (load.kind === 'loading') {
    // The page holds its shape while it waits: the counts show a dash and the rows are still
    // skeletons, so nothing jumps under the reader when the list lands.
    return (
      <div className="mtg">
        {head(null)}
        <div className="mtg-counts">
          <span><b className="big">–</b> yours</span>
          <span><b className="big">–</b> today</span>
        </div>
        <div className="mtg-filters">
          <div className="mtg-find wide"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" /></svg><span className="mtg-quiet">Reading your meetings…</span></div>
        </div>
        <div className="mtg-skeleton" aria-hidden="true"><i /><i /><i /><i /></div>
      </div>
    );
  }
  if (load.kind === 'error') return <div className="mtg">{head(null)}<Failed message={load.message} onRetry={read} /></div>;

  const { meetings: all, teamTab, companyTab } = load.view;
  // No control at all for a person the server has not switched a tab on for (R5): not a disabled
  // one, and an address asking for a tab they do not have shows their own list.
  const has: Record<ListTab, boolean> = { mine: true, team: teamTab, company: companyTab === true };
  const showing: ListTab = has[tab] ? tab : 'mine';
  const tabButton = (t: ListTab, label: string) => (
    <ActionButton reading type="button" role="tab" aria-selected={showing === t} className={showing === t ? 'on' : ''} onClick={() => choose(t)}>{label}</ActionButton>
  );
  const tabs = teamTab || has.company
    ? (
      <div className="mtg-looks" role="tablist">
        {tabButton('mine', 'Mine')}
        {teamTab ? tabButton('team', 'My Team') : null}
        {has.company ? tabButton('company', 'Company') : null}
      </div>
    )
    : null;

  const now = new Date();
  const today = all.filter((m) => bandOf(m.startedAt, now).key === 'today').length;
  const summarised = all.filter((m) => m.summaryId !== null).length;
  const rows = narrowBy(all, f);

  return (
    <div className="mtg">
      {head(tabs)}
      {showing !== 'mine'
        ? <TeamList key={showing} kind={showing} onOpen={(x) => onOpen(x, showing)} f={f} setF={setF} />
        : (
          <>
            {all.length > 0
              ? (
                <>
                  <div className="mtg-counts">
                    <span><b className="big">{all.length}</b> yours</span>
                    {today > 0 ? <span><b className="big">{today}</b> today</span> : null}
                    <span><b className="big">{summarised}</b> with a summary</span>
                  </div>
                  {/* BUGS.md M18: a list cut at its ceiling says so, so its counts and filters are read as the newest only. */}
                  {load.view.more ? <p className="mtg-note">The newest {all.length} are listed; your older calls are not, and the counts and filters cover these only.</p> : null}
                  <MeetingsBar f={f} setF={setF} rows={all} shown={rows.length} />
                </>
              )
              : null}
            {all.length === 0
              ? (
                <div className="mtg-empty">
                  <h2>Nothing recorded yet.</h2>
                  <p>
                    A meeting appears here once Cobrain has been in it — the Notetaker on an external call, or Teams on an
                    internal one — or once a call is shared with your group. It is listed as soon as the transcript arrives;
                    the summary follows.
                  </p>
                </div>
              )
              : rows.length === 0
                ? (
                  <div className="mtg-empty">
                    <h2>Nothing here answers that.</h2>
                    <p>{all.length} {all.length === 1 ? 'meeting was' : 'meetings were'} read; none of them matches what you asked for. Clear the search or choose every source.</p>
                  </div>
                )
                : <Bands rows={rows} onOpen={(x) => onOpen(x, 'mine')} openAll={isSet(f)} store={load.view.store.tenantId} />}
          </>
        )}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// One meeting: the player
// -------------------------------------------------------------------------------------------

type PlayerState =
  | { kind: 'idle' }
  | { kind: 'asking' }
  | { kind: 'playing'; how: 'embed' | 'video'; url: string; mintedAt: number; expiresInSeconds: number }
  | { kind: 'expired' }
  | { kind: 'unavailable'; reason: string };

export type FathomLink = { kind: 'asking' } | { kind: 'ready'; url: string } | { kind: 'none'; reason: string };

/**
 * A Fathom recording's share link (T77, B454), asked for when the page opens (the route answers it
 * only to someone the rails let read the call). One ask for the page: the recording's own Play and the
 * key takeaways, which open it at their minute (U196), both use it. Null for a call that is not Fathom's.
 */
function useFathomLink(m: MeetingDetail | null): FathomLink | null {
  const id = m && m.recording.available && m.recording.source === 'fathom' ? m.id : null;
  const [got, setGot] = useState<{ id: string; link: FathomLink } | null>(null);
  useEffect(() => {
    if (!id) return;
    let live = true;
    recordingLink(id)
      .then((r: RecordingView) => {
        if (!live) return;
        setGot({ id, link: r.available && r.kind === 'link' ? { kind: 'ready', url: r.link.url } : { kind: 'none', reason: r.available ? 'This recording cannot be opened from here.' : r.reason } });
      })
      .catch((e: unknown) => { if (live) setGot({ id, link: { kind: 'none', reason: messageOf(e) } }); });
    return () => { live = false; };
  }, [id]);
  if (!id) return null;
  return got && got.id === id ? got.link : { kind: 'asking' };
}

/**
 * A Fathom recording (T77, B454): opened in Fathom in a new tab. It does not expire, so there is no
 * countdown; it is Fathom's page, so nothing plays inside Cobrain.
 */
function FathomRecording({ link, length }: { link: FathomLink; length: string | null }) {
  if (link.kind === 'none') {
    return <div className="mtg-player mtg-player-line"><p className="mtg-player-caption">{link.reason}</p></div>;
  }
  return (
    <div className="mtg-player mtg-player-line">
      {link.kind === 'ready'
        ? <a className="mtg-play sm" href={link.url} target="_blank" rel="noopener noreferrer" aria-label="Watch the recording in Fathom"><PlayMark /></a>
        : <ActionButton reading type="button" className="mtg-play sm" disabled aria-label="Finding the recording"><PlayMark /></ActionButton>}
      <p className="mtg-player-caption">
        {link.kind === 'ready' ? `Recording${length ? `, ${length}` : ''}. Opens in Fathom.` : 'Finding the recording in Fathom…'}
      </p>
    </div>
  );
}

/** What the page may ask of its player from outside it: a key takeaway's claim (U196). */
interface PlayerApi { playFrom(ms: number): void }

/** Whether to move the page smoothly or at once: a person who asked for less motion gets none. */
const lessMotion = (): boolean => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Whether a minute lies past the end of a file `duration` seconds long, or inside its last second,
 * where there is nothing left to play. An unknown length (NaN before the browser has read it, Infinity
 * for a stream) is never past the end: only a length the file itself gave can send a minute elsewhere.
 */
export function pastTheEnd(duration: number, ms: number): boolean {
  return Number.isFinite(duration) && ms / 1000 >= duration - 1;
}

function Player({ m, fathom, api, onCannotPlayFrom }: {
  m: MeetingDetail; fathom: FathomLink | null;
  api: React.RefObject<PlayerApi | null>; onCannotPlayFrom: (ms: number) => void;
}) {
  const [state, setState] = useState<PlayerState>({ kind: 'idle' });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  // U196: the minute a takeaway asked for while the link was being fetched, taken when it arrives,
  // and whether this recording can start at a minute at all once the server has said what it is.
  const want = useRef<number | null>(null);
  const seekable = useRef<boolean | null>(null);
  // The minute the file was opened at, checked against its length once the browser has read it.
  const opening = useRef<number | null>(null);
  const ask = useCallback(() => {
    setState({ kind: 'asking' });
    recordingLink(m.id).then((r: RecordingView) => {
      const from = want.current;
      want.current = null;
      if (!r.available || r.kind === 'link') {
        seekable.current = false;
        setState({ kind: 'unavailable', reason: r.available ? 'This recording opens in Fathom.' : r.reason });
        if (from !== null) onCannotPlayFrom(from);
        return;
      }
      // Microsoft's embed takes no start (module 03 DISCOVERY), so a minute asked of it is shown in
      // the transcript instead, and the plate stays as it was rather than playing from 0:00.
      if (r.kind === 'embed' && from !== null) {
        seekable.current = false;
        setState({ kind: 'idle' });
        onCannotPlayFrom(from);
        return;
      }
      // A file opens at the minute asked for: the media fragment is read by the browser, never sent.
      const at = from !== null ? `#t=${Math.floor(from / 1000)}` : '';
      opening.current = r.kind === 'embed' ? null : from;
      // Cobrain's own copy: our route, behind our gate, with no expiry, so it plays like a file
      // and nothing counts down. A vendor's link is a loan and is timed; a copy is not.
      if (r.kind === 'stored') {
        seekable.current = true;
        setState({ kind: 'playing', how: 'video', url: `${r.link.url}${at}`, mintedAt: Date.now(), expiresInSeconds: 0 });
        return;
      }
      seekable.current = r.kind === 'video';
      setState({ kind: 'playing', how: r.kind, url: r.kind === 'video' ? `${r.link.url}${at}` : r.link.url, mintedAt: Date.now(), expiresInSeconds: r.expiresInSeconds });
      if (timer.current) clearTimeout(timer.current);
      // The link is Microsoft's for five minutes and then it is nobody's; the page says so rather than showing a dead frame.
      timer.current = setTimeout(() => setState({ kind: 'expired' }), r.expiresInSeconds * 1000);
    }).catch((e: unknown) => {
      const from = want.current;
      want.current = null;
      setState({ kind: 'unavailable', reason: messageOf(e) });
      if (from !== null) onCannotPlayFrom(from);
    });
  }, [m.id, onCannotPlayFrom]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // U196: play from a minute. A file that is already playing moves there; one not yet asked for is
  // asked for and opens there; a recording that cannot start at a minute sends it to the transcript.
  const playFrom = (ms: number): void => {
    if (seekable.current === false || (state.kind === 'playing' && state.how === 'embed')) { onCannotPlayFrom(ms); return; }
    const v = video.current;
    const playing = state.kind === 'playing' && state.how === 'video' && v !== null;
    if (playing && v.readyState >= 1 && pastTheEnd(v.duration, ms)) { onCannotPlayFrom(ms); return; }
    box.current?.scrollIntoView({ block: 'nearest', behavior: lessMotion() ? 'auto' : 'smooth' });
    if (playing) {
      const go = () => {
        if (pastTheEnd(v.duration, ms)) { onCannotPlayFrom(ms); return; }
        v.currentTime = ms / 1000;
        void v.play().catch(() => undefined);
      };
      if (v.readyState >= 1) go();
      else v.addEventListener('loadedmetadata', go, { once: true });
      return;
    }
    want.current = ms;
    if (state.kind !== 'asking') ask();
  };
  // A minute the file does not reach: a copy can be shorter than the call (a recording started late,
  // stopped early, or another occurrence's), and the browser would park at its last frame and say
  // nothing. The transcript opens at that minute instead, as for Microsoft's embed.
  const openedPastTheEnd = (v: HTMLVideoElement): void => {
    const from = opening.current;
    opening.current = null;
    if (from === null || !pastTheEnd(v.duration, from)) return;
    v.pause();
    v.currentTime = 0;
    onCannotPlayFrom(from);
  };
  // Every render, so the page always reaches this render's state and not the first one's.
  useEffect(() => { api.current = { playFrom }; });
  useEffect(() => () => { api.current = null; }, [api]);

  const length = lengthWords(m.minutes);
  // REQ-2026-10-13-14: Mike, of a Notetaker call held for someone else, "remove this block for exec".
  if (m.recording.hidden) return null;
  if (m.recording.available && m.recording.source === 'fathom') return <FathomRecording link={fathom ?? { kind: 'asking' }} length={length} />;
  if (!m.recording.available) {
    return (
      <div className="mtg-player mtg-player-line" ref={box}>
        <p className="mtg-player-caption">{m.recording.note ?? 'No recording is held for this meeting; only its transcript.'}</p>
      </div>
    );
  }
  if (state.kind === 'playing') {
    return (
      <div className="mtg-player" ref={box}>
        {state.how === 'video'
          ? <video ref={video} className="mtg-frame" src={state.url} controls autoPlay playsInline onLoadedMetadata={(e) => openedPastTheEnd(e.currentTarget)} />
          : <iframe className="mtg-frame" src={state.url} title={`Recording of ${m.title ?? 'the meeting'}`} allow="autoplay; fullscreen" allowFullScreen />}
        <p className="mtg-player-note">
          {state.expiresInSeconds === 0
            ? 'Playing from Cobrain’s own copy of the recording. Nothing runs out, and only the people the rails let read this call can open it.'
            : state.how === 'video'
              ? 'Playing from Recall, where the Notetaker’s recording lives. Cobrain keeps no copy.'
              : 'Playing from Microsoft, where the recording lives. Cobrain keeps no copy. This link lasts five minutes; press Play again if it stops.'}
        </p>
      </div>
    );
  }
  // B1564: the still, whatever the vendor was. This plate asked only when the vendor was Teams, so
  // every Notetaker call drew a blank one with its frame sitting in the bucket behind it -- Mike,
  // 2026-09-22, on a `recall` call whose still had been made two hours earlier: "The image here is
  // missing from all meeting notes". Nine of his nine Notetaker calls were in that state.
  const poster = stillUrl(m.vendor, m.id);
  return (
    <div className="mtg-player mtg-player-idle" ref={box} style={poster ? { backgroundImage: `url("${poster}")` } : undefined}>
      <ActionButton reading type="button" className="mtg-play" onClick={() => { want.current = null; ask(); }} disabled={state.kind === 'asking'} aria-label="Play the recording">
        <PlayMark />
      </ActionButton>
      <p className="mtg-player-caption">
        {state.kind === 'asking' ? 'Asking Microsoft for the recording…'
          : state.kind === 'expired' ? 'The five-minute link has run out. Press Play for a new one.'
            : state.kind === 'unavailable' ? state.reason
              : `Recording${length ? `, ${length}` : ''}${m.recording.source === 'teams' ? ', in the organiser’s OneDrive' : ''}`}
      </p>
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// One meeting: the tabs
// -------------------------------------------------------------------------------------------

function LongForm({ s, momentAt }: { s: NonNullable<MeetingDetail['summary']>; momentAt: (ms: number) => Moment | null }) {
  return (
    <div className="mtg-long">
      <p className="mtg-prose"><strong>{s.headline}</strong></p>
      {s.summary.split(/\n{2,}/).map((p, i) => <p key={i} className="mtg-prose">{p}</p>)}
      {s.decisions.length > 0 ? (
        <div className="mtg-block">
          <h3 className="mtg-h3">Decided</h3>
          <ul className="mtg-points">{s.decisions.map((d, i) => <li key={i}>{d.what}{d.by ? <em> — {d.by}</em> : null}<Minute at={s.pointAt?.decided[i]} momentAt={momentAt} /></li>)}</ul>
        </div>
      ) : null}
      {s.commitments.length > 0 ? (
        <div className="mtg-block">
          <h3 className="mtg-h3">Owed</h3>
          <ul className="mtg-points">
            {s.commitments.map((c, i) => <li key={i}>{c.what}{c.who ? <em> — {c.who}</em> : null}{c.when ? <em>, {c.when}</em> : null}<Minute at={s.pointAt?.owed[i]} momentAt={momentAt} /></li>)}
          </ul>
          {/* D60: nothing is a task until the extraction lane made one, with its evidence. */}
          <p className="mtg-note">Said in the meeting. Cobrain makes a task of its own accord only when it can point at the words that made it one; you can keep any of these as a task yourself from the action items.</p>
        </div>
      ) : null}
      {s.openQuestions.length > 0 ? (
        <div className="mtg-block">
          <h3 className="mtg-h3">Left open</h3>
          <ul className="mtg-points">{s.openQuestions.map((q, i) => <li key={i}>{q}<Minute at={s.pointAt?.open[i]} momentAt={momentAt} /></li>)}</ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Copy what is on the page to the clipboard (Mike, 2026-09-22, by pop-up: "Create a copy button
 * for both the transcript and the ai summary").
 *
 * The text is built HERE from the same values the page draws, not scraped from the DOM, so what
 * lands in the clipboard is what a person can read and nothing else: a turn the page is not
 * showing was still not shown to them, and a passage the rails withheld was never in `m` at all.
 * The transcript button copies EVERY turn, not the stretch on screen -- the paging is a kindness
 * to the browser, not a rule about what may leave the page.
 *
 * navigator.clipboard is absent on an insecure origin, throws when the document is not focused, and
 * is refused outright wherever the `clipboard-write` permission is denied — which is the state of
 * the browser pane these changes are driven in, so the async API alone could not be pressed and
 * proved at all. So there is a second way: a hidden textarea and `document.execCommand('copy')`,
 * which needs no permission, only the user gesture the click already is. It is deprecated and it
 * works everywhere; the modern call is tried first and this catches what it drops.
 *
 * If BOTH fail the button says so rather than passing silently: a copy button that looks like it
 * worked and did not is worse than one that says it could not.
 */
async function toClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* refused or unavailable; the older way may still be allowed */ }
  try {
    const pad = document.createElement('textarea');
    pad.value = text;
    // Off-screen but focusable: display:none or hidden cannot be selected, and a visible one
    // would flash and steal the scroll position.
    pad.setAttribute('readonly', '');
    pad.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
    document.body.appendChild(pad);
    pad.select();
    const ok = document.execCommand('copy');
    pad.remove();
    return ok;
  } catch { return false; }
}

function CopyButton({ text, what }: { text: string; what: string }) {
  const [said, setSaid] = useState<'idle' | 'done' | 'failed'>('idle');
  useEffect(() => {
    if (said === 'idle') return;
    const t = setTimeout(() => setSaid('idle'), 2200);
    return () => clearTimeout(t);
  }, [said]);
  const copy = async () => { setSaid(await toClipboard(text) ? 'done' : 'failed'); };
  return (
    <ActionButton reading type="button" className="mtg-copy" onClick={copy} aria-label={`Copy ${what}`}>
      {said === 'done' ? 'Copied' : said === 'failed' ? 'Could not copy' : `Copy ${what}`}
    </ActionButton>
  );
}

/** The summary as plain text, in the order the page draws it. */
export function summaryAsText(m: MeetingDetail): string {
  const s = m.summary;
  if (!s) return '';
  const out: string[] = [];
  if (m.title) out.push(m.title);
  out.push(s.headline, '');
  const b = s.brief;
  if (b) {
    out.push('Meeting purpose', b.purpose, '');
    if (b.takeaways.length > 0) { out.push('Key takeaways'); for (const t of b.takeaways) out.push(`- ${t}`); out.push(''); }
    // U381: the summary mail's line, where the page draws its card.
    if (m.airtime && m.airtime.length > 0) out.push(airtimeLine(m.airtime), '');
    for (const t of b.topics) { out.push(t.title); for (const p of t.points) out.push(`- ${p}`); out.push(''); }
  }
  out.push('The full account', s.summary, '');
  if (s.decisions.length > 0) { out.push('Decided'); for (const d of s.decisions) out.push(`- ${d.what}${d.by ? ` (${d.by})` : ''}`); out.push(''); }
  if (s.commitments.length > 0) { out.push('Owed'); for (const c of s.commitments) out.push(`- ${c.what}${c.who ? ` - ${c.who}` : ''}${c.when ? `, ${c.when}` : ''}`); out.push(''); }
  if (s.openQuestions.length > 0) { out.push('Left open'); for (const q of s.openQuestions) out.push(`- ${q}`); out.push(''); }
  if (!b && m.airtime && m.airtime.length > 0) out.push(airtimeLine(m.airtime), '');
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd();
}

/** Every turn as plain text, stamped and named, in the order they were spoken. */
export function transcriptAsText(turns: MeetingDetail['turns']): string {
  return turns.map((t) => `${stampWords(t.at)}  ${t.speaker ?? 'Unknown'}: ${said(t.text, t.speaker)}`).join('\n');
}

/**
 * What pressing a minute of this call does (U196): an address it can be opened at, what it will do
 * in words, and the act itself when it happens on this page. `act` null is a link that leaves (Fathom).
 */
export interface Moment { href: string; title: string; act: (() => void) | null }

/**
 * What a minute of this call can do, by what its recording is (U196): play in the page's player (a
 * file: Cobrain's copy or the Notetaker's; a Teams call with only Microsoft's embed finds that out
 * when asked and goes to the transcript), open in Fathom there, or open the transcript there. Null
 * draws the claim plain: nothing to go to, or Fathom's link not back yet (it is a moment later).
 */
export function momentKind(x: Pick<MeetingDetail, 'recording' | 'turns'>, fathom: FathomLink | null): 'play' | 'fathom' | 'transcript' | null {
  if (x.recording.available && x.recording.source === 'fathom') {
    if (fathom?.kind === 'ready') return 'fathom';
    if (fathom?.kind === 'asking') return null;
  } else if (x.recording.available) {
    return 'play';
  }
  return x.turns.length > 0 ? 'transcript' : null;
}

/**
 * A key takeaway's bold claim as a link to where it was discussed (U196). A link, never a button:
 * a button is laid out as one box and would push the rest of the bullet past its widest line, where
 * a link wraps with the text. A plain click acts on this page; a click that asks for a new tab or
 * window gets one, at the address, which the page honours (?t=).
 */
function Claim({ text, moment }: { text: string; moment: Moment | null }) {
  if (!moment) return <strong>{text}</strong>;
  return <a className="mtg-claim" {...momentLink(moment)}><strong>{text}</strong></a>;
}

/** What pressing a link to a minute does: acts on this page, or opens Fathom in a tab of its own (U196). */
function momentLink(moment: Moment) {
  const leaves = moment.act === null;
  return {
    href: moment.href, title: moment.title,
    ...(leaves ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
    onClick: (e: ReactMouseEvent<HTMLAnchorElement>) => {
      if (leaves || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      moment.act!();
    },
  };
}

/**
 * Every other summary point's minute (module 10 U199; Mike, 2026-09-24, by pop-up: "Link every point"
 * and "Yes, same everywhere"): the clock after the point's words, doing what a takeaway's claim does.
 * Nothing where the point has no minute, or where there is nothing to go to yet (Fathom's link is a
 * moment later).
 */
function Minute({ at, momentAt, inProse = false }: { at: number | null | undefined; momentAt: (ms: number) => Moment | null; inProse?: boolean }) {
  if (at === null || at === undefined) return null;
  const moment = momentAt(at);
  return moment ? <>{' '}<a className={`mtg-at mtg-min${inProse ? ' mtg-minute-prose' : ''}`} {...momentLink(moment)}>{inProse ? <PlayMark /> : null}{clockWords(at)}{inProse ? ' in' : null}</a></> : null;
}

/** The call's stored read, outside the tabs. The only write is a person's press after a failure. */
export function HowItWent({ id, read, joined, momentAt, onAsked }: {
  id: string; read: MeetingHowItWent | null | undefined; joined: boolean;
  momentAt: (ms: number) => Moment | null; onAsked: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const asking = useRef(false);
  const again = async (): Promise<void> => {
    if (asking.current) return;
    asking.current = true;
    setBusy(true);
    setError(null);
    try {
      await askHowItWentAgain(id);
      onAsked();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      asking.current = false;
      setBusy(false);
    }
  };
  // An older API may not carry the field yet; it draws the same absence as null.
  if (!read) return null;
  return (
    <section className={`mtg-went${joined ? ' joined' : ''}`} aria-label="How it went">
      <h2>How it went</h2>
      {read.state === 'written' ? read.paragraphs.map((paragraph, i) => {
        const moments = new Map(paragraph.moments.map((m) => [m.id, m.atMs]));
        const parts = paragraph.text.split(/(\{\{m\d+\}\})/g);
        return <p key={i}>{parts.map((part, j) => {
          const marker = /^\{\{(m\d+)\}\}$/.exec(part);
          // The punctuation straight after a minute stays on its line with it (a phone wrapped ". The tone" alone).
          const tied = j > 0 && /^\{\{m\d+\}\}$/.test(parts[j - 1]!) ? (/^[.,;:!?)]+/.exec(part)?.[0] ?? '') : '';
          if (!marker) return <Fragment key={j}>{part.slice(tied.length)}</Fragment>;
          const at = moments.get(marker[1]!);
          const after = /^[.,;:!?)]+/.exec(parts[j + 1] ?? '')?.[0] ?? '';
          return at !== undefined && Number.isFinite(at) && at >= 0
            ? <span key={j} className="mtg-went-tie"><Minute at={at} momentAt={momentAt} inProse />{after}</span> : after;
        })}</p>;
      }) : null}
      {read.state === 'writing' ? <div role="status">
        <p className="mtg-went-state">Being written from the call. It shows here in a few minutes.</p>
        <div className="mtg-went-skeleton" aria-hidden="true"><i /><i /></div>
      </div> : null}
      {read.state === 'failed' ? <>
        <div className="mtg-went-failed">
          <span>It could not be written from this call.</span>
          <ActionButton type="button" className="mtg-went-again" disabled={busy} onClick={() => { void again(); }}>Try again</ActionButton>
        </div>
        {error ? <p className="mtg-went-error" role="alert">{error}</p> : null}
      </> : null}
      {read.state === 'no_transcript' ? <p className="mtg-went-state">There is no transcript of this call, so there is nothing to read.</p> : null}
    </section>
  );
}

/** The summary mail's line, word for word (apps/api/src/meeting/airtime.ts airtimeLine): every speaker, nothing cut. */
export function airtimeLine(shares: NonNullable<MeetingDetail['airtime']>): string {
  return `Airtime: ${shares.map((x) => `${x.speaker} ${x.pct}%`).join(', ')}`;
}

/** A speaker's time in whole minutes, "under 1 min" below half a minute (U381). */
export function airtimeMinutes(ms: number): string {
  return ms < 30_000 ? 'under 1 min' : `${Math.round(ms / 60_000)} min`;
}

/**
 * Module 10 U381 (Mike, 8 Oct 2026: "shows the amount of airtime, or percentage that each person has spoken"): one row
 * a speaker, the longest first, with a bar of their share, the percentage and the minutes, from the transcript's
 * timings alone. Figures and names only, no word about anyone's share. Nothing when there is no timed segment.
 */
export function Airtime({ m }: { m: Pick<MeetingDetail, 'airtime' | 'minutes'> }) {
  const rows = m.airtime;
  if (!rows || rows.length === 0) return null;
  const speech = rows.reduce((n, x) => n + x.ms, 0);
  return (
    <section className="mtg-block card mtg-airtime" aria-label="Airtime">
      <h2>Airtime</h2>
      <ul className="mtg-air">
        {rows.map((x, i) => (
          <li key={i} className="mtg-air-row">
            <span className="mtg-air-who">{x.speaker}</span>
            <span className="mtg-air-bar" aria-hidden="true"><span style={{ width: `${x.pct}%` }} /></span>
            <span className="mtg-air-fig">{x.pct}% · {airtimeMinutes(x.ms)}</span>
          </li>
        ))}
      </ul>
      <p className="mtg-note">
        Share of the time anyone was speaking, from the transcript’s timings: {airtimeMinutes(speech)} of speech{m.minutes !== null && m.minutes !== undefined ? ` in a ${m.minutes} min call` : ''}.
      </p>
    </section>
  );
}

export function SummaryTab({ m, momentAt }: { m: MeetingDetail; momentAt: (ms: number) => Moment | null }) {
  const [longOpen, setLongOpen] = useState(false);
  const s = m.summary;
  if (!s) {
    // U381: the airtime needs only the transcript's timings, so it shows before the summary is written.
    return (
      <>
      <section className="mtg-block card">
        <h2>{hasTranscript(m.transcriptStatus) ? 'Not summarised yet' : 'Why there is no summary'}</h2>
        <p className="mtg-prose">
          {hasTranscript(m.transcriptStatus)
            ? 'The transcript is here and Cobrain is reading it. The summary appears once the whole call has been read — a summary of part of a meeting would be worse than none.'
            : 'No transcript arrived for this meeting, so there is nothing to summarise. Cobrain summarises a call from its transcript alone; it never writes one from the title or the diary entry.'}
        </p>
      </section>
      <Airtime m={m} />
      </>
    );
  }
  const b = s.brief;
  return (
    <>
      <div className="mtg-copybar"><CopyButton text={summaryAsText(m)} what="the summary" /></div>
      {b ? (
        <>
          <section className="mtg-block card">
            <h2>Meeting purpose</h2>
            <p className="mtg-purpose">{b.purpose}</p>
          </section>
          <section className="mtg-block card">
            <h2>Key takeaways</h2>
            <ul className="mtg-points mtg-takeaways">
              {b.takeaways.map((t, i) => {
                // The claim is bolded up to its first colon, but not a colon inside a time ("10:00am"),
                // which bolded "goes out at 10:" (B1696; the summary mail's leadOf has the same rule).
                const cut = /(?<!\d):|:(?!\d)/.exec(t)?.index ?? -1;
                // U196: the bold claim, and only it, goes to where it was discussed.
                const at = s.takeawayAt?.[i] ?? null;
                return cut > 0 && cut < 90
                  ? <li key={i}><Claim text={`${t.slice(0, cut)}:`} moment={at !== null ? momentAt(at) : null} />{t.slice(cut + 1)}</li>
                  : <li key={i}>{t}<Minute at={at} momentAt={momentAt} /></li>;
              })}
            </ul>
          </section>
          <Airtime m={m} />
          {b.topics.length > 0 ? (
            <section className="mtg-block card">
              <h2>Topics</h2>
              <div className="mtg-topics">
                {b.topics.map((t, i) => (
                  <div key={i} className="mtg-topic">
                    <h3 className="mtg-h3">{t.title}</h3>
                    <ul className="mtg-points">{t.points.map((p, j) => <li key={j}>{p}<Minute at={s.pointAt?.topics[i]?.[j]} momentAt={momentAt} /></li>)}</ul>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          <section className="mtg-block card">
            {/* U32: what Cobrain did is said in words, never a model name or how the lane read it. */}
            <h2>The full account<ActionButton reading type="button" className="mtg-toggle" aria-expanded={longOpen} onClick={() => setLongOpen((v) => !v)}>{longOpen ? 'Hide it' : 'Show it'}</ActionButton></h2>
            {longOpen
              ? <LongForm s={s} momentAt={momentAt} />
              : <p className="mtg-note">Everything Cobrain drew from reading the whole transcript: {s.decisions.length} decided, {s.commitments.length} owed, {s.openQuestions.length} left open.</p>}
          </section>
        </>
      ) : (
        <section className="mtg-block card">
          <h2>What this was about</h2>
          <p className="mtg-note">The brief for this meeting has not been written yet; this is the full account.</p>
          <LongForm s={s} momentAt={momentAt} />
        </section>
      )}
      {b ? null : <Airtime m={m} />}

    </>
  );
}

/** How many turns are drawn before "Show more": a long call runs past a thousand. */
const TURN_PAGE = 150;

/**
 * A transcript turn's text sometimes repeats the speaker ("Mike Cobain: ...") because that is how
 * the source wrote it. The speaker already has its own column, so the repeat is dropped.
 */
function said(text: string, speaker: string | null): string {
  if (!speaker) return text;
  const lead = `${speaker}:`;
  return text.startsWith(lead) ? text.slice(lead.length).trimStart() : text;
}

function Mark({ text, term }: { text: string; term: string }) {
  if (term === '') return <>{text}</>;
  const i = text.toLowerCase().indexOf(term);
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="mtg-mark">{text.slice(i, i + term.length)}</mark><Mark text={text.slice(i + term.length)} term={term} /></>;
}

/**
 * The turn a jump to `ms` lands on: the last whose time is known and not after it. A turn with no
 * time (past the first passage this reader's rails keep from them, B431) is never the one: it was
 * read as 0:00, so every jump on such a transcript landed on its last turn. And a minute past the
 * last time this reader has, with untimed turns after it, lies somewhere among them: no turn is
 * marked rather than the wrong one. A minute here is always a turn's own start, so a turn that
 * begins exactly at it is that turn.
 */
export function jumpTurn(turns: MeetingDetail['turns'], ms: number | null): number {
  if (ms === null) return -1;
  let h = -1;
  for (let i = 0; i < turns.length; i++) { const at = turns[i]!.at; if (at !== null && at <= ms) h = i; }
  if (h >= 0 && ms > turns[h]!.at! && turns[h + 1] !== undefined && turns[h + 1]!.at === null) return -1;
  return h;
}

function TranscriptTab({ turns, jumpTo }: { turns: MeetingDetail['turns']; jumpTo: number | null }) {
  const target = useRef<HTMLLIElement | null>(null);
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(TURN_PAGE);
  const term = q.trim().toLowerCase();
  useEffect(() => { setShown(TURN_PAGE); }, [term]);
  // A jump from an action item or a takeaway must reach its turn even when it is past the first stretch.
  const hit = useMemo(() => jumpTurn(turns, jumpTo), [turns, jumpTo]);
  useEffect(() => { if (hit >= 0 && hit + 1 > shown) setShown(hit + TURN_PAGE); }, [hit, shown]);
  useEffect(() => { target.current?.scrollIntoView({ block: 'center' }); }, [jumpTo, shown]);

  if (turns.length === 0) return <section className="mtg-block card"><p className="mtg-note">No transcript was stored for this meeting.</p></section>;
  const found = term === '' ? turns : turns.filter((t) => said(t.text, t.speaker).toLowerCase().includes(term) || (t.speaker ?? '').toLowerCase().includes(term));
  const drawn = found.slice(0, shown);
  return (
    <section className="mtg-block card">
      <div className="mtg-copybar"><CopyButton text={transcriptAsText(turns)} what={`all ${turns.length} turns`} /></div>
      <div className="mtg-find">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" /></svg>
        <ActionInput reading type="search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find words in this transcript" placeholder="Find words in this transcript" />
        {q !== '' ? <ActionButton reading type="button" className="clear" onClick={() => setQ('')}>Clear</ActionButton> : null}
      </div>
      <p className="mtg-note">
        {term === ''
          ? `${turns.length} turns, as spoken. ${drawn.length < found.length ? `Showing the first ${drawn.length}.` : ''}`
          : found.length === 0
            ? `Nothing in these ${turns.length} turns says that.`
            : `${found.length} of ${turns.length} turns say that.${drawn.length < found.length ? ` Showing the first ${drawn.length}.` : ''}`}
      </p>
      <ol className="mtg-turns">
        {drawn.map((t, i) => {
          const real = term === '' ? i : turns.indexOf(t);
          return (
            <li key={real} ref={real === hit ? target : null} className={real === hit ? 'mtg-turn-hit' : undefined}>
              <span className="mtg-stamp">{stampWords(t.at)}</span>
              <span className="mtg-speaker">{t.speaker ?? 'Unknown'}</span>
              <span className="mtg-said"><Mark text={said(t.text, t.speaker)} term={term} /></span>
            </li>
          );
        })}
      </ol>
      {drawn.length < found.length
        ? <ActionButton reading type="button" className="mtg-more" onClick={() => setShown((n) => n + TURN_PAGE)}>Show the next {Math.min(TURN_PAGE, found.length - drawn.length)} turns</ActionButton>
        : null}
    </section>
  );
}


/**
 * The count beside "Tasks Cobrain made" (BUGS.md M24, X25): the server lists at most 50 of a call's tasks, so a call with
 * more reads "50 of 63" rather than a count that stops at the length of the list.
 */
export function tasksCount(m: Pick<MeetingDetail, 'tasks' | 'tasksTotal'>): string {
  const total = Math.max(m.tasksTotal ?? m.tasks.length, m.tasks.length);
  return total > m.tasks.length ? `${m.tasks.length} of ${total}` : String(m.tasks.length);
}

/** The meeting's own task list (BUGS.md M24, M28, M34): a done task stays listed, marked Done in the muted words. */
export function MeetingTasks({ m }: { m: Pick<MeetingDetail, 'tasks' | 'tasksTotal'> }) {
  if (m.tasks.length === 0) return null;
  return (
    <section className="panel">
      <h2>Tasks Cobrain made<span className="sp" /><span className="n">{tasksCount(m)}</span></h2>
      <ul className="mtg-points">
        {m.tasks.map((t) => (
          <li key={t.id}>{t.statement}{t.ownerName ? <em> — {t.ownerName}</em> : null}{t.dueAt ? <em>, due {dayMonth(new Date(t.dueAt))}</em> : null}{t.done ? <span className="mtg-quiet"> Done</span> : null}</li>
        ))}
      </ul>
    </section>
  );
}

/** A week out, as the date field wants it, for the due date a new task must carry. */
function weekOut(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return dateField(d.toISOString());
}

/** An instant as the date field wants it, on the reader's own calendar day. */
function dateField(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** BUGS.md M28: the reader's own task from the action item at `index`, or null (none, or a server older than it). */
export function itemTaskOf(m: Pick<MeetingDetail, 'actionTasks'>, index: number): MeetingItemTask | null {
  const t = m.actionTasks?.[index];
  return t && typeof t.taskId === 'string' && (t.state === 'suggested' || t.state === 'open' || t.state === 'done') ? t : null;
}

type AddState = { kind: 'shut' } | { kind: 'open'; due: string; priority: number } | { kind: 'saving' } | { kind: 'done'; id: string } | { kind: 'failed'; message: string };

/**
 * One action item, and the choice to keep it as a task (Mike, 2026-09-20: "action items need to
 * have the option to add them to tasks"). Nothing is created by pressing "Add to tasks": that
 * opens the two questions a task must answer, and "Add it" is the act. D60 is untouched -- Cobrain
 * still makes a task only from words it can point at; this is the person making one deliberately.
 */
export function ActionItem({ a, index, store, meeting, onOpenAt, go, onKept }: {
  a: MeetingActionItem; index: number; store: string | null; meeting: MeetingDetail;
  onOpenAt: ((ms: number) => void) | null; go: (path: string) => void; onKept: () => void;
}) {
  // BUGS.md M28: the reader's own task from this item, as the server read it. One already theirs reads as added, as it
  // did straight after the press; Cobrain's suggestion from it is what "Add it" accepts, so its form opens at its date
  // and priority.
  const kept = itemTaskOf(meeting, index);
  const [add, setAdd] = useState<AddState>(() => (kept && kept.state !== 'suggested' ? { kind: 'done', id: kept.taskId } : { kind: 'shut' }));
  useEffect(() => {
    if (kept && kept.state !== 'suggested') setAdd((x) => (x.kind === 'shut' ? { kind: 'done', id: kept.taskId } : x));
  }, [kept?.taskId, kept?.state]);
  const opened = (): AddState => ({ kind: 'open', due: kept?.state === 'suggested' ? dateField(kept.dueAt) : weekOut(), priority: kept?.state === 'suggested' ? kept.priority : 3 });
  const save = async (due: string, priority: number) => {
    if (!store) return;
    setAdd({ kind: 'saving' });
    try {
      const when = new Date(`${due}T17:00:00`);
      const r = await addTask({
        store, title: a.what, dueAt: when.toISOString(), priority,
        notes: `From the meeting "${meeting.title ?? 'Untitled meeting'}" on ${whenWords(meeting.startedAt)}${a.who ? `. Said to be ${a.who}'s.` : ''}${a.at_ms !== null ? ` At ${clockWords(a.at_ms)} in the call.` : ''}`,
        ...(meeting.summary ? { meetingId: meeting.id, summaryId: meeting.summary.id, item: index } : {}),
      });
      setAdd({ kind: 'done', id: r.id });
      onKept();
    } catch (e: unknown) {
      setAdd({ kind: 'failed', message: messageOf(e) });
    }
  };
  return (
    <li>
      <span className="mtg-action-what">{a.what}</span>
      <span className="mtg-action-meta">
        {a.who ? <span className="mtg-who">{a.who}</span> : <span>Nobody named</span>}
        {a.at_ms !== null && onOpenAt
          ? <ActionButton reading type="button" className="mtg-at" onClick={() => onOpenAt(a.at_ms!)} title="Open the transcript here">{clockWords(a.at_ms)} in the call</ActionButton>
          : a.at_ms !== null
            ? <span className="mtg-at quiet">{clockWords(a.at_ms)} in the call</span>
            : <span className="mtg-at quiet">Not found in the transcript</span>}
        {add.kind === 'shut' && store
          ? <ActionButton type="button" className="mtg-keep" onClick={() => setAdd(opened())}>Add to tasks</ActionButton>
          : null}
        {add.kind === 'done'
          ? <span className="mtg-kept">Added to your tasks{kept?.state === 'done' && kept.taskId === add.id ? <span className="mtg-quiet"> Done</span> : null} <ActionButton reading type="button" className="link" onClick={() => go(`/tasks/${add.id}`)}>Open it</ActionButton></span>
          : null}
      </span>
      {add.kind === 'open' || add.kind === 'saving'
        ? (
          <span className="mtg-add">
            <label>Due<ActionInput type="date" value={add.kind === 'open' ? add.due : weekOut()} disabled={add.kind === 'saving'}
              onChange={(e) => setAdd((x) => (x.kind === 'open' ? { ...x, due: e.target.value } : x))} /></label>
            <label>Priority<ActionSelect value={add.kind === 'open' ? add.priority : 3} disabled={add.kind === 'saving'}
              onChange={(e) => setAdd((x) => (x.kind === 'open' ? { ...x, priority: Number(e.target.value) } : x))}>
              <option value={1}>1, first</option><option value={2}>2</option><option value={3}>3</option><option value={4}>4, last</option>
            </ActionSelect></label>
            <ActionButton type="button" className="opt lead" disabled={add.kind === 'saving'}
              onClick={() => { if (add.kind === 'open') void save(add.due, add.priority); }}>{add.kind === 'saving' ? 'Adding' : 'Add it'}</ActionButton>
            <ActionButton reading type="button" className="link" disabled={add.kind === 'saving'} onClick={() => setAdd({ kind: 'shut' })}>Cancel</ActionButton>
          </span>
        )
        : null}
      {add.kind === 'failed'
        ? (
          <span className="mtg-add-failed" role="alert">
            {add.message}
            <ActionButton type="button" className="link" onClick={() => setAdd(opened())}>Try again</ActionButton>
          </span>
        )
        : null}
    </li>
  );
}

// -------------------------------------------------------------------------------------------
// One meeting: the page
// -------------------------------------------------------------------------------------------

/**
 * The rundown of the company the call is filed under, in the side column, each section folded to its first item
 * (pipeline RC5; U354): the same component and words as the Calendar brief. A call filed under no company draws nothing
 * here; the route's reason is not worth a panel beside a recording.
 */
function MeetingRundown({ id, store, go }: { id: string; store: string | null; go: (path: string) => void }) {
  const [view, setView] = useState<RundownView | null>(null);
  useEffect(() => {
    if (!store) return;
    let cancelled = false;
    setView(null);
    fetchRundown(store, { meeting: id }).then((v) => { if (!cancelled) setView(v); }, () => { /* the page stands without it */ });
    return () => { cancelled = true; };
  }, [id, store]);
  return view?.rundown ? <Rundown data={view.rundown} cut="side" go={go} /> : null;
}

/**
 * BUGS.md M45: "Visible to Conor" on a call Mike was on and Conor was not, and "Visible to Mike" the other way round,
 * off until turned on (Mike, 2026-10-07). Drawn only when the database offers a switch: to nobody else, on no other
 * call. A refusal is shown in the database's own words, and the switch stays as it was.
 */
export function VisibleTo({ id }: { id: string }) {
  const [switches, setSwitches] = useState<VisibleSwitch[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setSwitches([]);
    setError(null);
    visibleTo(id).then((r) => { if (live) setSwitches(r.switches); }).catch(() => { if (live) setSwitches([]); });
    return () => { live = false; };
  }, [id]);
  if (switches.length === 0) return null;
  const press = async (s: VisibleSwitch): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setSwitches((await setVisibleTo(id, s.to, !s.on)).switches);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nothing was changed: the switch could not be reached.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel mtg-visible">
      {switches.map((s) => {
        const who = firstNameOf(s.name);
        return (
          <div key={s.to} className="mtg-visible-row">
            <ActionButton type="button" role="switch" aria-checked={s.on} disabled={busy} className={s.on ? 'mtg-switch on' : 'mtg-switch'} onClick={() => void press(s)}>
              <i aria-hidden="true" />
            </ActionButton>
            <span className="mtg-visible-label">Visible to {who}</span>
            <p className="mtg-note">{s.on ? `${who} reads this call as if he had been on it.` : 'Only the people on the call read it.'}</p>
          </div>
        );
      })}
      {error ? <p className="mtg-note mtg-visible-error" role="alert">{error}</p> : null}
    </section>
  );
}

/**
 * BUGS.md M46: "Keep to the people on it", under "Visible to Conor" where that shows, on the call's page only (Mike by
 * pop-up, 7 Oct about 13:15 CEST). Drawn only for a colleague who was on the call; pressed by anyone else, or undone by
 * anyone but the one who pressed it, the database refuses in words and the words are shown.
 */
export function KeepToPeople({ id }: { id: string }) {
  const [state, setState] = useState<KeepState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setState(null);
    setError(null);
    keepState(id).then((r) => { if (live) setState(r.state); }).catch(() => { if (live) setState(null); });
    return () => { live = false; };
  }, [id]);
  if (!state) return null;
  const byOther = state.kept && !state.keptByMe;
  const press = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setState((await setKeep(id, !state.kept)).state);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nothing was changed: the button could not be reached.');
    } finally {
      setBusy(false);
    }
  };
  const note = state.kept
    ? byOther ? `${state.keptByName ? firstNameOf(state.keptByName) : 'A colleague on it'} kept it to the people on it; only they can undo that.` : 'Only the people on the call read it. Press again to undo.'
    : state.heldWaiting
      ? state.askedMe ? 'Cobrain is keeping it to the people on it: part of it is about pay or a person\'s job. Keep or release it from your list.' : 'Cobrain is keeping it to the people on it: part of it is about pay or a person\'s job. Its organiser is asked whether to release it.'
      : state.held ? 'Kept to the people on it: its organiser chose to keep it.' : 'Others read it as the rails allow.';
  return (
    <section className="panel mtg-visible mtg-keep">
      <div className="mtg-visible-row">
        <ActionButton type="button" role="switch" aria-checked={state.kept} disabled={busy || byOther} className={state.kept ? 'mtg-switch on' : 'mtg-switch'} onClick={() => void press()}>
          <i aria-hidden="true" />
        </ActionButton>
        <span className="mtg-visible-label">Keep to the people on it</span>
        <p className="mtg-note">{note}</p>
      </div>
      {error ? <p className="mtg-note mtg-visible-error" role="alert">{error}</p> : null}
    </section>
  );
}

export function MeetingPage({ id, onBack, go, store }: { id: string; onBack: () => void; go: (path: string) => void; store: string | null }) {
  const [load, setLoad] = useState<OneLoad>({ kind: 'loading' });
  const [tab, setTab] = useState<'summary' | 'transcript'>('summary');
  const [jumpTo, setJumpTo] = useState<number | null>(null);
  const one = useCallback(() => fetchMeeting(id), [id]);
  const { read, quiet } = useReadTwoWays(one, setLoad);
  useEffect(read, [read]);
  useLiveRefresh(quiet);
  useEffect(() => { setTab('summary'); setJumpTo(null); }, [id]);
  const openAt = useCallback((ms: number) => { setJumpTo(ms); setTab('transcript'); }, []);
  const view = load.kind === 'ready' ? load.view : null;
  const fathom = useFathomLink(view);
  const player = useRef<PlayerApi | null>(null);

  /** U196: what pressing this minute does, by what the recording is; null draws the claim plain. */
  const momentAt = (x: MeetingDetail, ms: number): Moment | null => {
    const at = clockWords(ms);
    switch (momentKind(x, fathom)) {
      case 'fathom': return fathom?.kind === 'ready' ? { href: fathomAt(fathom.url, ms), title: `Opens in Fathom at ${at}`, act: null } : null;
      case 'play': return { href: hereAt(ms), title: `Play from ${at}`, act: () => (player.current ? player.current.playFrom(ms) : openAt(ms)) };
      case 'transcript': return { href: hereAt(ms), title: `Open the transcript at ${at}`, act: () => openAt(ms) };
      default: return null;
    }
  };

  // U196: a minute in the address (?t=SECONDS, the call page's convention) is where the page opens,
  // once per meeting. A Fathom call has nothing to do here: its minutes are links into Fathom.
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (!view || opened.current === view.id) return;
    opened.current = view.id;
    const t = new URLSearchParams(window.location.search).get('t');
    if (t === null || !/^\d{1,6}$/.test(t)) return;
    if (view.recording.available && view.recording.source === 'fathom') return;
    momentAt(view, Number(t) * 1000)?.act?.();
    // Only `view`: once per meeting, on its first read, with the page as it stands then.
  }, [view]);

  const back = <p className="back-line"><ActionButton reading type="button" className="dotted" onClick={onBack}>← Meetings</ActionButton></p>;
  if (load.kind === 'loading') return <div className="mtg mtg-one">{back}<div className="mtg-skeleton" aria-hidden="true"><i /><i /></div></div>;
  if (load.kind === 'error') return <div className="mtg mtg-one">{back}<Failed message={load.message} onRetry={read} /></div>;

  const m = load.view;
  const length = lengthWords(m.minutes);
  // Opened from My Team (B444, B1521) or Company (B1649), a meeting the reader was not on may have
  // reached them by a share rule OR by their own tier -- both tabs take both -- so the label says
  // whose call it is and not how it arrived. "Shared with your group" would claim a share that may
  // not exist.
  const from = new URLSearchParams(window.location.search).get('from');
  const fromTeam = from === 'team' || from === 'company';
  const via = fromTeam && m.via === 'group' ? 'Your team’s call' : viaWords(m.via);
  const items = m.summary?.actionItems ?? [];
  const recording = <Player m={m} fathom={fathom} api={player} onCannotPlayFrom={openAt} />;

  return (
    <div className="mtg mtg-one">
      {back}
      <div className="mtg-head">
        <h1>{m.title ?? 'Untitled meeting'}</h1>
        <div className="mtg-facts">
          <span className="plate quiet">{whenWords(m.startedAt)}</span>
          <Tag tone="quiet">{captureWords(m.vendor)}</Tag>
          {length ? <Tag tone="quiet">{length}</Tag> : null}
          {m.isExternal ? <Tag tone="ext">External</Tag> : null}
          {via ? <span className="via">{via}</span> : null}
        </div>
      </div>
      <div className="mtg-cols">
        <div className="mtg-main">
          {m.howItWent ? <div className={`mtg-recording-read${!m.recording.hidden ? ' joined' : ''}`}>
            {recording}
            <HowItWent key={m.id} id={m.id} read={m.howItWent} joined={!m.recording.hidden} momentAt={(ms) => momentAt(m, ms)} onAsked={() => {
              setLoad((current) => current.kind === 'ready' && current.view.id === m.id
                ? { kind: 'ready', view: { ...current.view, howItWent: { state: 'writing' } } } : current);
            }} />
          </div> : recording}
          {/* A tab onto nothing is worse than no tab: with no transcript stored, only Summary is drawn. */}
          {m.turns.length > 0
            ? (
              <div className="mtg-tabs" role="tablist">
                <ActionButton reading type="button" role="tab" aria-selected={tab === 'summary'} className={tab === 'summary' ? 'on' : ''} onClick={() => setTab('summary')}>Summary</ActionButton>
                <ActionButton reading type="button" role="tab" aria-selected={tab === 'transcript'} className={tab === 'transcript' ? 'on' : ''} onClick={() => setTab('transcript')}>
                  Transcript<span className="count">{m.turns.length} turns</span>
                </ActionButton>
              </div>
            )
            : null}
          {tab === 'summary' || m.turns.length === 0 ? <SummaryTab m={m} momentAt={(ms) => momentAt(m, ms)} /> : <TranscriptTab turns={m.turns} jumpTo={jumpTo} />}
        </div>

        <aside className="mtg-side">
          <VisibleTo id={m.id} />
          <KeepToPeople id={m.id} />
          <MeetingRundown id={m.id} store={store} go={go} />
          <section className="panel">
            <h2>Attendees</h2>
            {m.attendees.length === 0
              ? <p className="mtg-note">{m.speakers.length > 0 ? m.speakers.join(', ') : 'Nobody is recorded for this meeting.'}</p>
              : (
                <ul className="mtg-people-list">
                  {m.attendees.map((a, i) => (
                    <li key={i}><strong>{a.name}</strong>{a.jobTitle ? <span className="mtg-title-of">{a.jobTitle}</span> : null}</li>
                  ))}
                </ul>
              )}
          </section>

          {(m.siblings ?? []).length > 0 ? (
            <section className="panel">
              <h2>Other recordings of this meeting</h2>
              {m.siblings.map((x) => (
                <ActionButton reading key={x.id} type="button" className="mtg-related" onClick={() => go(`/meetings/${x.id}`)}>
                  <strong>{captureWords(x.vendor)}</strong>
                  <span>{whenWords(x.startedAt)}<em>{x.turns} {x.turns === 1 ? 'turn' : 'turns'}</em></span>
                </ActionButton>
              ))}
            </section>
          ) : null}

          <section className="panel">
            <h2>Related</h2>
            {m.related
              ? (
                <ActionButton reading type="button" className="mtg-related" onClick={() => go(`/meetings/${m.related!.id}`)}>
                  <strong>{m.related.title ?? 'Untitled meeting'}</strong>
                  <span>The meeting before this one<em>{whenWords(m.related.startedAt)}</em></span>
                </ActionButton>
              )
              : <p className="mtg-note">No earlier meeting with this title on your list.</p>}
          </section>

          <section className="panel">
            <h2>Action items{items.length > 0 ? <><span className="sp" /><span className="n">{items.length}</span></> : null}</h2>
            {!m.summary
              ? <p className="mtg-note">Action items are drawn from the transcript when the summary is written.</p>
              : !m.summary.brief
                ? <p className="mtg-note">Listed once the short version is written. Until then they are under Owed in the full account.</p>
                : items.length === 0
                  ? <p className="mtg-note">The meeting put nothing on anybody.</p>
                  : (
                    <ul className="mtg-actions">
                      {items.map((a, i) => (
                        <ActionItem key={`${m.summary?.id ?? ''}:${i}`} a={a} index={i} store={store} meeting={m} go={go}
                          onOpenAt={m.turns.length > 0 ? openAt : null} onKept={() => void quiet()} />
                      ))}
                    </ul>
                  )}
          </section>

          {/* D60: what the extraction lane actually made, with evidence, and what a person added from an action item
              (BUGS.md M28), are the tasks from this meeting; a done one stays, marked Done. */}
          <MeetingTasks m={m} />
        </aside>
      </div>
    </div>
  );
}

/** The whole surface: the list, or one meeting when the path names it. */
export function Meetings({ id, go, store }: { id?: string; go: (path: string) => void; store?: string | null }) {
  // A meeting opened from the My Team or Company tab carries ?from=team or ?from=company, so the
  // page can say whose call it is, and the list's own address as ?list= (its tab, the team-mate and
  // everything the bar holds, B1957), so its Back returns to the list as it was left. One parameter,
  // because the list's own `from` (the first day) and the page's `t` are not the meeting page's.
  // Read at the moment of use, not at render: the list narrows with replaceState, which does not
  // render this component again.
  const here = new URLSearchParams(window.location.search);
  const fromParam = here.get('from');
  const from: ListTab = fromParam === 'team' || fromParam === 'company' ? fromParam : 'mine';
  const back = (): string => {
    const list = new URLSearchParams(window.location.search).get('list');
    if (list !== null) return list === '' ? '/meetings' : `/meetings?${list}`;
    // An address from before ?list= carried the team-mate alone.
    const who = new URLSearchParams(window.location.search).get('who');
    return from === 'mine' ? '/meetings' : `/meetings?tab=${from}${who ? `&who=${encodeURIComponent(who)}` : ''}`;
  };
  const open = (x: string, tab: ListTab): void => {
    const list = window.location.search.replace(/^\?/, '');
    go(`/meetings/${x}${withParams('', { from: tab === 'mine' ? '' : tab, list })}`);
  };
  return id
    ? <MeetingPage id={id} onBack={() => go(back())} go={go} store={store ?? null} />
    : <MeetingsList onOpen={open} />;
}
